import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { useHttp } from '@/hooks/http';

import type { IzziSaleOrder, PaginatedIzziSaleOrders } from '@/domain/izzi';

const DEFAULT_PAGE_SIZE = 20;

const EMPTY_PAGE: PaginatedIzziSaleOrders = {
  data: [],
  total: 0,
  page: 0,
  pageSize: DEFAULT_PAGE_SIZE,
};

export const useOrdersSection = () => {
  const http = useHttp();
  const [isLoading, setIsLoading] = useState(false);
  const [downloadingOrderId, setDownloadingOrderId] = useState<string | null>(null);
  const [data, setData] = useState<PaginatedIzziSaleOrders>(EMPTY_PAGE);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [searchCustomerSo, setSearchCustomerSo] = useState('');
  const [appliedCustomerSo, setAppliedCustomerSo] = useState('');

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);

    try {
      const result = await http.izzi.getOrders({
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        ...(appliedCustomerSo ? { customerSo: appliedCustomerSo } : {}),
      });
      setData(result);
    } catch {
      setData(EMPTY_PAGE);
      toast.error('Error al cargar las órdenes de Izzi.');
    } finally {
      setIsLoading(false);
    }
  }, [http.izzi, paginationModel, appliedCustomerSo]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const onSearchCustomerSoChange = (value: string) => {
    setSearchCustomerSo(value);
  };

  const onSearch = () => {
    setAppliedCustomerSo(searchCustomerSo.trim());
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const onClearSearch = () => {
    setSearchCustomerSo('');
    setAppliedCustomerSo('');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const onPaginationModelChange = (model: { page: number; pageSize: number }) => {
    setPaginationModel(model);
  };

  const onDownloadShippingReceipt = async (order: IzziSaleOrder) => {
    setDownloadingOrderId(order.id);

    try {
      await http.izzi.downloadShippingReceipt(order);
      toast.success('Acuse de envío descargado correctamente.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error al generar el acuse de envío';
      toast.error(message);
    } finally {
      setDownloadingOrderId(null);
    }
  };

  return {
    data,
    isLoading,
    downloadingOrderId,
    paginationModel,
    searchCustomerSo,
    appliedCustomerSo,
    onSearchCustomerSoChange,
    onSearch,
    onClearSearch,
    onPaginationModelChange,
    onDownloadShippingReceipt,
    fetchOrders,
  };
};
